import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShieldAlert, FileCheck, Bot, CreditCard, LogOut,
  Menu, X, Moon, Sun, ChevronDown,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview", tid: "nav-overview" },
  { to: "/dashboard/findings", icon: ShieldAlert, label: "Findings", tid: "nav-findings" },
  { to: "/dashboard/compliance", icon: FileCheck, label: "Compliance", tid: "nav-compliance" },
  { to: "/dashboard/copilot", icon: Bot, label: "AI Engineer", tid: "nav-copilot" },
  { to: "/dashboard/billing", icon: CreditCard, label: "Billing", tid: "nav-billing" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  const SidebarInner = () => (
    <>
      <div className="px-5 py-5 border-b border-line">
        <button onClick={() => navigate("/")}><Logo /></button>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end} data-testid={n.tid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-cyber/10 text-cyber" : "text-muted hover:text-ink hover:bg-[var(--surface-2)]"
              }`}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-line">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-[var(--surface-2)]">
          <div className="w-8 h-8 rounded-lg bg-cyber/15 flex items-center justify-center text-cyber font-bold text-sm shrink-0 overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "A").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink truncate">{user?.name || "Agent"}</p>
            <p className="text-xs text-muted truncate">{user?.company || user?.email}</p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="text-muted hover:text-crit p-1.5">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-bg flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-line bg-[var(--surface)] fixed inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-64 flex flex-col border-r border-line bg-[var(--surface)] z-50">
            <SidebarInner />
          </aside>
        </>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 border-b border-line bg-[var(--bg)]/80 backdrop-blur-xl flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-toggle"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-line text-ink">
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-2 text-xs font-mono text-muted">
              <span className="w-2 h-2 rounded-full bg-secure live-dot" /> Continuous monitoring active
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="dash-theme-toggle"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-line text-muted hover:text-cyber">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-line hover:border-cyber">
                <div className="w-7 h-7 rounded-md bg-cyber/15 flex items-center justify-center text-cyber font-bold text-xs overflow-hidden">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "A").toUpperCase()}
                </div>
                <ChevronDown size={14} className="text-muted" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--surface)] border-line">
                <DropdownMenuItem onClick={() => navigate("/dashboard/billing")} className="text-ink">Billing</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} className="text-crit">Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
